import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { registerWaiting } from "../../lib/api";
import { parseKnockHandle } from "../../lib/knock-nav";
import { handleExistsQuery, myWaitingQuery } from "../../lib/queries";
import { useLandInSpot } from "../waiting/land";
import { DoorSearch } from "../knocks/door-search";
import { Button } from "../ui/button";
import { NobFace } from "./mascot";
import { Sheet } from "./surfaces";
import { useShellUi } from "./state";

export const KNOCK_TITLE = "Knock on a door";
export const KNOCK_LEDE = "You talk to their doorman until they let you through.";

/** What a row offers, and what it says once it has been taken. */
export const RESERVE = "Hold a spot";
export const RESERVED = "Spot reserved";
export const RESERVE_PLACEHOLDER = "One line: what do you want from them?";
export const RESERVE_SEND = "Reserve";
export const RESERVE_FAILED = "That did not go through. Try that again.";

/**
 * The row for an X handle that has NEITHER a door NOR a queue (WR FR-G1).
 *
 * It is the third answer the lookup can give and the reference build has it:
 * somebody types a name they know from X, that account exists, and nothing at
 * all has happened here yet. Offering the spot is the only useful thing to say
 * — and it is the moment the waiting room was built for, since a queue has to
 * start with somebody.
 */
/**
 * THREE ANSWERS, THREE TITLES (a G-2r, wave 17b).
 *
 * The row carried ONE hard-coded headline over all three — "Nobody at @maya
 * yet" — and let only the muted line underneath vary. So the one answer that
 * leads somewhere, a live door, was announced in the loudest text on the row as
 * an absence, with the truth whispered under it. The subtitle was made to vary
 * and the title was not.
 *
 * The existing subtitles stand; each title now says which answer this is:
 *
 *   a door here          → GHOST_DOOR_TITLE  over GHOST_DOOR_OPEN
 *   on X, no door yet    → GHOST_UNMET       over GHOST_OFFER / GHOST_HELD
 *   nobody on X          → GHOST_TITLE       over GHOST_UNKNOWN
 *
 * They are functions rather than lead/suffix pairs because the handle's
 * position differs between them — it opens one sentence and sits inside
 * another — and a pair of literals can only express one shape. The row renders
 * the handle in mono wherever the pattern puts it (`HandleRow`).
 */
export function ghostTitle(handle: string): string {
  return `Nobody at @${handle}`;
}
/** There IS a door there. The loudest text on the row says so. */
export function doorTitle(handle: string): string {
  return `@${handle} has a door`;
}
/** On X, with no door here. The absence is ours, and the title names whose. */
export function unmetTitle(handle: string): string {
  return `Nob hasn't met @${handle} yet`;
}
export const GHOST_OFFER = "Hold a spot. You knock the moment they set up.";
export const GHOST_HELD = "You're on the doormat. Nob tells you when they hang a door.";
/** A handle nobody on X holds. Not an error — a plain answer to a plain question. */
export const GHOST_UNKNOWN = "No X account answers to that handle.";
/** There IS a door there. The address road, said as what it is. */
export const GHOST_DOOR_OPEN = "This door is live. Press enter to knock.";

/**
 * The way out of your own list.
 *
 * ONE VIEW, SINCE RULING R-0916-3. There were two under this roof — the search,
 * and the Leaderboard its own tail led to: a ranked list of the handles most
 * people were waiting at, each row carrying its count. ToU 2.2 §5B.2 publishes
 * "no ranking, league table or ordered list of doors or of people, in any
 * form", and §5A.5 no count at an unopened handle, so the view, its room at
 * `/board`, and the endpoint behind both are gone. What remains is the search,
 * which was always the road somebody with a name in mind took.
 *
 * **THERE IS NO GATE ON THIS DIALOG** (ruling R-0825-4). It used to replace the
 * search box with a plans offer for anybody whose period had no room — which
 * meant a Free reader could not look anybody up and could not hold a spot,
 * both of which cost nothing and neither of which is a knock.
 * Conversion happens AT THE KNOCK, where the money is actually about to move:
 * the door surface asks for a plan when a knock is refused, and the server
 * refuses it, which it would have done regardless of what this dialog drew.
 * Gating the lookup only hid the product from the people it was trying to sell
 * to. Do not put the gate back.
 *
 * THREE ROADS OUT OF THE BOX (rulings R-0904-3, R-0905-2), and they are three
 * different questions asked of three different places:
 *
 *   the TYPEAHEAD — a name or a handle, on every keystroke, answering with
 *                   everybody Nob already knows (see `DoorSearch`).
 *   X             — the same words, on a press, answering with X's own people
 *                   search on the reader's own token (see `DoorSearch`).
 *   the IDENTITY  — a typed `@handle`, or a bare handle offered as the address
 *                   row, resolved one at a time — which reaches an X user who
 *                   has neither a door nor a queue without anybody's index in
 *                   the way (see `TypedHandle`, `DoorSearch`).
 *
 * A typed `@handle` still never touches either search endpoint, which is the
 * whole reason a door nobody has indexed remains knockable.
 */
export function NewKnockSheet() {
  const ui = useShellUi();

  /**
   * Picking a door opens the knock HERE, in the console (owner, 2026-08-26).
   *
   * It used to navigate to `/@handle` — out of the shell, onto the door poster,
   * through the intake's five questions and back again. That road is the one a
   * stranger needs and this reader is not one: they are already inside the
   * product, and the door they just picked is a conversation they are about to
   * have. So the sheet closes onto a compose thread and nothing leaves this
   * address. No knock target is written either: that key exists to survive a
   * round trip out of the origin, and there is no longer a trip to survive.
   */
  const go = (handle: string) => {
    ui.closeSheet();
    ui.openCompose(handle);
  };

  return (
    <Sheet title={KNOCK_TITLE} lede={KNOCK_LEDE} onClose={ui.closeSheet}>
      <DoorSearch
        onGo={go}
        waiting={(query, isAddress) => <>{isAddress && <TypedHandle typed={query} onGo={go} />}</>}
      />
    </Sheet>
  );
}

/* --------------------------------------------------------- the typed handle */

/**
 * One handle, asked about by name.
 *
 * THE POINT OF THIS ROW is the person who has NOT signed up. The typeahead
 * cannot reach them: it knows doors that exist and the operator's own target
 * list, and since ruling R-0916-3 it does not read `demand_records` at all — a
 * row that appeared because somebody had pre-knocked would announce the
 * pre-knock. So somebody typing a handle they know from X would, without this,
 * be told "no doors match that", which is true and useless. The identity port
 * knows the account is real; a spot is the thing worth offering.
 *
 * IT IS ONE LOOKUP, NOT A SEARCH, and that distinction is the reason WR
 * Appendix E's enumerable-directory objection does not apply: a caller must
 * already know the handle to ask, and the answer is about that one account.
 * Nothing here can be swept.
 *
 * AND IT IS FREE (ruling R-0910-2). This row fires as somebody types, so it may
 * only ever ask the free question — Nob's own records, its miss cache, then X's
 * keyless embed endpoint. It used to run X's PAID user lookup on every typed
 * handle, which is the cost the ruling exists to end.
 *
 * A LOOKUP IN FLIGHT DRAWS NOTHING. A row that appeared as "nobody there yet"
 * and then became a live door a moment later would be a row that lied first.
 */
function TypedHandle({ typed, onGo }: { typed: string; onGo: (handle: string) => void }) {
  const handle = parseKnockHandle(typed);
  const { data, isPending } = useQuery({
    ...handleExistsQuery(handle ?? ""),
    enabled: handle !== null,
    retry: false,
  });

  if (!handle || isPending || !data) return null;

  // A door outranks everything: the waiting room's job at this handle is over,
  // and the address road is what the person is already on.
  if (data.door) {
    return (
      <HandleRow
        handle={handle}
        title={doorTitle(handle)}
        line={GHOST_DOOR_OPEN}
        onClick={() => onGo(handle)}
      />
    );
  }
  // Suppressed reads exactly like unresolved on purpose (FR-G7.4): a handle
  // whose owner asked to be taken out must not be distinguishable from one that
  // never existed, or the removal would be a signal of its own. X having said
  // nothing at all (`exists === null`) draws no row either — the offer of a spot
  // is a claim about somebody existing, and we do not have one.
  if (data.exists !== true) {
    return <HandleRow handle={handle} title={ghostTitle(handle)} line={GHOST_UNKNOWN} />;
  }

  return <GhostRow handle={handle} />;
}

/** A handle that exists on X and has no door here. The spot is the offer. */
function GhostRow({ handle }: { handle: string }) {
  const land = useLandInSpot();
  const { data: mine = [] } = useQuery({ ...myWaitingQuery(), retry: false });
  // STILL HELD, not merely remembered: a withdrawn registration rides the list
  // as a stopped conversation now (F-27), and "Spot reserved" over a spot
  // somebody let go of would be the row lying about it. A stopped spot gets the
  // offer again, and re-registering is legal.
  const held = mine.some(
    (r) => r.handle.toLowerCase() === handle.toLowerCase() && r.state === "active",
  );
  const [open, setOpen] = useState(false);

  return (
    <section>
      <HandleRow
        handle={handle}
        // Held or not, the answer is the same one: this person is on X and Nob
        // has not met them. What differs is what to do about it, which is the
        // line underneath.
        title={unmetTitle(handle)}
        // A spot already held opens the thread holding it rather than offering
        // one twice: the row is a way IN to that conversation, not a dead end.
        line={held ? GHOST_HELD : GHOST_OFFER}
        onClick={held ? () => land(handle, "") : () => setOpen((v) => !v)}
      />
      {open && !held && <ReserveField handle={handle} onDone={land} />}
    </section>
  );
}

/**
 * The handle inside a title, in mono — one sentence, one source.
 *
 * The three titles differ in where the handle falls, so the row finds it rather
 * than being handed the sentence in pieces: the string stays the single thing a
 * test reads and a reader reads, and the typography still marks the handle out
 * from the words around it.
 */
function MonoHandle({ text, handle }: { text: string; handle: string }) {
  const at = `@${handle}`;
  const cut = text.indexOf(at);
  if (cut < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, cut)}
      <span className="font-mono">{at}</span>
      {text.slice(cut + at.length)}
    </>
  );
}

/**
 * The row itself — a handle and one line about it, and nothing else.
 *
 * NO DISPLAY NAME, NO AVATAR, NO FOLLOWER COUNT, even though the identity port
 * just answered with all three. This is H2 reaching the one surface that could
 * quietly break it: an unclaimed handle belongs to somebody
 * who has not signed up, so anything beyond the handle they were seen at is a
 * profile of a person who never agreed to have one.
 */
function HandleRow({
  handle,
  title,
  line,
  onClick,
}: {
  handle: string;
  /** Which of the three answers this is — see `ghostTitle` and its two siblings. */
  title: string;
  line: string;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <NobFace mood="bored" size="md" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold" data-handle-row-title>
          <MonoHandle text={title} handle={handle} />
        </span>
        <span className="block truncate text-xs text-muted">{line}</span>
      </span>
    </>
  );
  const shape =
    "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-75";

  if (!onClick) {
    return <div className={`${shape} opacity-70`}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${shape} hover:bg-hover
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70`}
    >
      {inner}
    </button>
  );
}

/**
 * The line, and it is not optional (FR-G3).
 *
 * Registering asks what you want from them, the server enforces three
 * characters and screens it, and the door owner reads it as their held spot the
 * day they open. One field, shared by the two rows that offer a spot — the
 * a typed handle nobody has queued at yet is the one road left to it, and the
 * spot thread's "Hold the spot again" is the other — because a second copy is
 * how the two would come to ask for different things.
 */
function ReserveField({
  handle,
  onDone,
}: {
  handle: string;
  /** The line as it was registered. */
  onDone: (handle: string, line: string) => void;
}) {
  const [line, setLine] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const reserve = () => {
    const text = line.trim();
    if (text.length < 3 || busy) return;
    setBusy(true);
    setFailed(false);
    void registerWaiting(handle, text).then((result) => {
      setBusy(false);
      // ALREADY_REGISTERED is not a failure to report: the spot being offered
      // is already held, and the line that STANDS is the one that came back
      // rather than the one just typed — nothing was overwritten.
      if (!result.ok && result.code !== "ALREADY_REGISTERED") {
        setFailed(true);
        return;
      }
      onDone(handle, result.ok ? text : (result.line ?? ""));
    });
  };

  return (
    <>
      <div className="flex items-center gap-2 px-2 pb-2">
        <label htmlFor={`reserve-${handle}`} className="sr-only">
          {RESERVE_PLACEHOLDER}
        </label>
        <input
          id={`reserve-${handle}`}
          type="text"
          maxLength={280}
          autoFocus
          placeholder={RESERVE_PLACEHOLDER}
          value={line}
          onChange={(e) => setLine(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              reserve();
            }
          }}
          className="h-9 min-w-0 flex-1 rounded-full bg-search px-3 text-xs text-text
                     placeholder:text-fainter focus:outline-none focus:ring-2 focus:ring-focus/70"
        />
        <Button
          type="button"
          className="h-9 shrink-0 px-3 text-xs"
          disabled={busy || line.trim().length < 3}
          onClick={reserve}
        >
          {RESERVE_SEND}
        </Button>
      </div>
      {failed && (
        <p role="alert" className="px-2 pb-2 text-xs text-muted">
          {RESERVE_FAILED}
        </p>
      )}
    </>
  );
}
